<template>
  <div>
    <div
      class="flex h-13"
      @mouseover="hoverState = true"
      @mouseleave="hoverState = false"
    >
      <div class="sticky left-0 z-10 flex pl-6 bg-slate-100">
        <div
          class="flex items-center flex-shrink-0 w-56 px-4 transition-all duration-300 border-b border-l border-r border-slate-400"
          :class="[
            isLast ? 'rounded-bl' : '',
            hoverState ? 'bg-hover-state' : 'bg-pure-white'
          ]"
        >
          <BlTextBody2
            :title="client.name"
            :text="client.name"
            class="truncate text-slate-1000"
          />
        </div>
      </div>
      <div
        class="flex items-center flex-shrink-0 w-40 px-4 transition-all duration-300 border-b border-slate-400"
        :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
      >
        <BlChip :chip="client.status" dense />
      </div>
      <div
        class="flex items-center flex-shrink-0 w-40 px-4 transition-all duration-300 border-b border-slate-400"
        :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
      >
        <BlTextBody2 v-if="!client.branches || !client.branches.length" text="-" />
        <div v-else class="flex items-center gap-2">
          <div
            v-for="(branch, idx) in client.branches.slice(0, 2)"
            :key="idx"
            :title="branch.name || '-'"
            class="flex items-center justify-center px-2 truncate rounded h-7 min-w-8 max-w-20"
            :class="{ 'bg-slate-300': !branch.background_color }"
            :style="{ backgroundColor: branch.background_color || 'unset' }"
          >
            <BlTextBody2
              :text="branch.name || '-'"
              class="truncate"
              :class="{ 'text-slate-800': !branch.color }"
              :style="{ color: branch.color || 'unset' }"
            />
          </div>
          <div class="relative" v-if="client.branches.slice(2).length">
            <div
              class="flex items-center justify-center cursor-pointer"
              @click="moreBranchesOpened = !moreBranchesOpened"
            >
              <BlBubbleCount
                :count="`+ ${client.branches.slice(2).length}`"
                class="h-7 hover:bg-light-purple-100"
              />
            </div>
            <div
              v-if="moreBranchesOpened"
              class="absolute top-0 right-0 z-50 flex flex-col gap-1 px-1 py-1 mt-10 border rounded bg-pure-white border-slate-400"
            >
              <div
                v-for="(branch, idx) in client.branches.slice(2)"
                :key="idx"
                :title="branch.name || '-'"
                class="flex px-4 py-2 text-left truncate rounded min-w-40 max-w-64"
              >
                <BlTextBody2 class="truncate" :text="branch.name || '-'" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        class="flex items-center flex-shrink-0 w-40 px-4 transition-all duration-300 border-b border-slate-400"
        :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
      >
        <BlTextBody2 :text="client.available_credits" />
      </div>
      <div
        class="flex items-center flex-shrink-0 w-40 px-4 transition-all duration-300 border-b border-slate-400"
        :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
      >
        <BlTextBody2 :text="client.reserved_credits" />
      </div>
      <div
        class="flex items-center flex-shrink-0 w-40 px-4 transition-all duration-300 border-b border-slate-400"
        :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
      >
        <BlTextBody2 :text="client.unused_credits" />
      </div>
      <div
        class="flex items-center flex-shrink-0 w-40 px-4 transition-all duration-300 border-b border-slate-400"
        :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
      >
        <BlTextBody2 :text="client.scheduled_sessions" />
      </div>
      <div
        class="flex items-center flex-shrink-0 w-40 px-4 transition-all duration-300 border-b border-slate-400"
        :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
      >
        <BlTextBody2 :text="expirationDate" />
      </div>
      <div
        class="flex items-center flex-shrink-0 w-40 px-4 transition-all duration-300 border-b border-slate-400"
        :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
      >
        <BlTextBody2 :text="rescheduleCredits" />
      </div>
      <div
        class="flex items-center flex-shrink-0 w-40 gap-1 px-4 transition-all duration-300 border-b border-slate-400"
        :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
      >
        <BlTextBody2 :text="currentCenter.currency_sym" />
        <BlTextBody2 :text="balance" />
      </div>
      <div class="sticky right-0 z-10 flex flex-grow pr-6 bg-slate-100">
        <div
          class="flex items-center justify-end flex-grow flex-shrink-0 w-20 px-4 transition-all duration-300 border-b border-r border-slate-400"
          :class="[
            isLast ? 'rounded-br' : '',
            hoverState ? 'bg-hover-state' : 'bg-pure-white'
          ]"
        >
          <router-link
            :to="{
              name: 'ClientTab',
              params: {
                id: client.id,
                tab: 'credit',
                redirect_target: `/finance`,
                redirect_label: 'Finance'
              }
            }"
          >
            <BlInputButton
              title="View client profile"
              label="View"
              type="outlined"
              dense
            />
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { displayDate, displayPrice } from "@/plugins/helper.js";

import BlTextBody2 from "../../BlTextBody2.vue";
import BlChip from "../../BlChip.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlBubbleCount from "../../BlBubbleCount.vue";

export default {
  name: "CreditsManagementItem",
  components: {
    BlTextBody2,
    BlChip,
    BlInputButton,
    BlBubbleCount
  },
  props: {
    client: { type: Object, required: true },
    isLast: { type: Boolean, default: false }
  },
  data() {
    return {
      hoverState: false,
      moreBranchesOpened: false

    };
  },
  computed: {
    ...mapGetters(["currentUser", "currentCenter"]),
    expirationDate() {
      const date = this.client.expiration_date;
      return this.displayDate({
        date,
        format: "DD MMM YYYY",
        empty: "No expiration date"
      });
    },
    rescheduleCredits() {
      const arr = this.client.reschedule_credits;
      const sum = arr.reduce((sum, i) => (sum += i || 0), 0);
      if (arr.includes(null)) {
        if (sum > 0) return `∞ + ${sum}`;
        return `∞`;
      }
      return sum;
    },
    balance() {
      const settings = this.currentCenter.currency_settings;
      const price = this.client.current_balance;
      return this.displayPrice({ settings, price });
    }
  },
  watch: {
    moreBranchesOpened(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closeHandlerMoreBranches);
        }, 10);
      }
    }
  },
  async mounted() {},
  methods: {
    displayDate,
    displayPrice,
    closeHandlerMoreBranches() {
      window.removeEventListener("click", this.closeHandlerMoreBranches);
      this.moreBranchesOpened = false;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/finance/creditsManagement/Item.vue